import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import AllocationChart from '../components/portfolio/AllocationChart';
import PortfolioMetrics from '../components/portfolio/PortfolioMetrics';
import { RefreshCw, AlertCircle, Download, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { formatCurrency } from '../utils/constants';

const Portfolio = () => {
  const { get, post, loading, error } = useApi();
  const [portfolio, setPortfolio] = useState(null);
  const [hasRiskProfile, setHasRiskProfile] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [investmentAmount, setInvestmentAmount] = useState(100000);

  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const data = await get('/portfolio');
        setPortfolio(data);
        if (data.totalInvestment) setInvestmentAmount(data.totalInvestment);
      } catch (err) {
        if (err.response?.status === 404) {
          try { await get('/risk/profile'); } catch { setHasRiskProfile(false); }
        }
      } finally {
        setInitialLoad(false);
      }
    };
    loadPortfolio();
  }, [get]);

  const handleGenerate = async () => {
    try {
      const data = await post('/portfolio/generate', { totalInvestment: Number(investmentAmount) });
      setPortfolio(data);
    } catch { /* error set by useApi */ }
  };

  const handleDownloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/portfolio/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Algodhan_Report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report');
    }
  };

  if (initialLoad) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!hasRiskProfile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Risk Profile Required</h2>
        <p className="text-gray-500 mb-6">Please complete the risk assessment before generating a portfolio.</p>
        <Link to="/risk-assessment" className="btn-primary">Take Risk Assessment</Link>
      </div>
    );
  }

  // Asset mix pie data
  const assetMixData = portfolio?.assetMix
    ? Object.entries(portfolio.assetMix).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v }))
    : [];

  // Feature importance bar data
  const featureData = portfolio?.featureImportance
    ? Object.entries(portfolio.featureImportance)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, value]) => ({ name, importance: Math.round(value * 10000) / 100 }))
    : [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Your Portfolio</h1>
          <p className="text-gray-500">Your personalized investment allocation</p>
        </div>
        <div className="flex items-center gap-3">
          {portfolio && (
            <button onClick={handleDownloadReport} className="btn-secondary flex items-center gap-2 text-sm">
              <Download className="h-4 w-4" /> PDF
            </button>
          )}
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(e.target.value)}
              className="input-field w-32 text-sm"
              placeholder="Investment ₹"
              min={1000}
            />
            <button onClick={handleGenerate} className="btn-primary flex items-center gap-2" disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {portfolio ? 'Regenerate' : 'Generate'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {portfolio ? (
        <div className="space-y-6">
          <PortfolioMetrics
            metrics={portfolio.metrics}
            riskCategory={portfolio.riskProfile?.riskCategory}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stock Allocation Pie */}
            <AllocationChart allocations={portfolio.allocations} title="Holdings Allocation" />

            {/* Asset Mix Pie */}
            {assetMixData.length > 0 && (
              <div className="card">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Asset Class Mix</h4>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={assetMixData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value"
                        label={({ name, value }) => `${name} ${value}%`}>
                        {assetMixData.map((_, i) => (
                          <Cell key={i} fill={['#3b82f6', '#22c55e', '#f59e0b', '#94a3b8'][i]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${v}%`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* Stock Table with Amounts */}
          <div className="card">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Allocation Details</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Stock</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Ticker</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Class</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Weight</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">Amount (₹)</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.allocations.map((stock, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 font-medium text-gray-800">{stock.name}</td>
                      <td className="py-3 px-2 text-gray-500">{stock.ticker}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          stock.assetClass === 'equity' ? 'bg-blue-100 text-blue-700' :
                          stock.assetClass === 'debt' ? 'bg-green-100 text-green-700' :
                          stock.assetClass === 'gold' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {stock.assetClass || 'equity'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right font-semibold text-primary-600">{stock.weight}%</td>
                      <td className="py-3 px-2 text-right font-semibold text-gray-800">
                        {formatCurrency(stock.amount || 0)}
                      </td>
                      <td className="py-3 px-2 w-32">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-primary-500 h-2 rounded-full" style={{ width: `${Math.min(stock.weight * 3, 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Feature Importance */}
          {featureData.length > 0 && (
            <div className="card">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary-600" /> Key Stock Selection Factors
              </h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={featureData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="importance" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card text-center py-12">
          <EmptyIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Portfolio Yet</h3>
          <p className="text-gray-500 mb-6">Click "Generate" to create your personalized portfolio</p>
          <button onClick={handleGenerate} className="btn-primary" disabled={loading}>
            {loading ? 'Generating...' : 'Generate Portfolio'}
          </button>
        </div>
      )}
    </div>
  );
};

const EmptyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2 20l7.5-7.5L13 16l9-9m0 0h-6m6 0v6" />
  </svg>
);

export default Portfolio;
